import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Meal } from '../types/nutrition';
import { searchableFoods } from '../data/foodDatabase';

// Pre-logged meals for demonstration
const initialMeals: Meal[] = [
  {
    id: '1',
    type: 'breakfast',
    time: new Date().setHours(8, 30).toString(),
    foods: [
      { ...searchableFoods.find(f => f.id === 'oatmeal')!, servings: 1 },
      { ...searchableFoods.find(f => f.id === 'banana')!, servings: 1 },
      { ...searchableFoods.find(f => f.id === 'greek-yogurt')!, servings: 1 }
    ]
  },
  {
    id: '2',
    type: 'lunch',
    time: new Date().setHours(13, 0).toString(),
    foods: [
      { ...searchableFoods.find(f => f.id === 'chicken-breast')!, servings: 1 },
      { ...searchableFoods.find(f => f.id === 'brown-rice')!, servings: 1 },
      { ...searchableFoods.find(f => f.id === 'sweet-potato')!, servings: 1 }
    ]
  },
  {
    id: '3',
    type: 'snack',
    time: new Date().setHours(16, 0).toString(),
    foods: [
      { ...searchableFoods.find(f => f.id === 'almonds')!, servings: 1 },
      { ...searchableFoods.find(f => f.id === 'banana')!, servings: 1 }
    ]
  }
];

interface MealState {
  meals: Meal[];
  todaysStats: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
  addMeal: (meal: Omit<Meal, 'id'>) => void;
  updateMeal: (id: string, meal: Partial<Meal>) => void;
  removeMeal: (id: string) => void;
  calculateDailyStats: () => void;
}

export const useMealStore = create<MealState>()(
  persist(
    (set, get) => ({
      meals: initialMeals,
      todaysStats: {
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0
      },

      addMeal: (meal) => {
        const newMeal = {
          ...meal,
          id: crypto.randomUUID()
        };

        set((state) => ({
          meals: [...state.meals, newMeal]
        }));

        get().calculateDailyStats();
      },

      updateMeal: (id, updates) => {
        set((state) => ({
          meals: state.meals.map((meal) =>
            meal.id === id ? { ...meal, ...updates } : meal
          )
        }));

        get().calculateDailyStats();
      },

      removeMeal: (id) => {
        set((state) => ({
          meals: state.meals.filter((meal) => meal.id !== id)
        }));

        get().calculateDailyStats();
      },

      calculateDailyStats: () => {
        const state = get();
        const today = new Date().toISOString().split('T')[0];
        
        const todaysMeals = state.meals.filter(
          meal => meal.time.split('T')[0] === today
        );

        const stats = todaysMeals.reduce(
          (acc, meal) => {
            meal.foods.forEach(food => {
              acc.calories += food.calories * (food.servings || 1);
              if (food.protein) acc.protein += food.protein * (food.servings || 1);
              if (food.carbs) acc.carbs += food.carbs * (food.servings || 1);
              if (food.fat) acc.fat += food.fat * (food.servings || 1);
            });
            return acc;
          },
          { calories: 0, protein: 0, carbs: 0, fat: 0 }
        );

        set({ todaysStats: stats });
      }
    }),
    {
      name: 'meal-storage',
      version: 1
    }
  )
);